import { createClient } from '@connectrpc/connect'
import { createGrpcWebTransport } from '@connectrpc/connect-web'
import { Streamline, RentalPriceChoice, PriceChangeStatus, Role } from '@streamline/kss-grpc-client/connect'
import { environment } from '@/config/environment'
import { createMaintenanceInterceptor } from './maintenance'

function authHeaders(jwt) {
  return { authentication: jwt }
}

// protobuf-es v2 returns int64/uint64 as bigint and message instances carry a
// `$typeName`. The previous google-protobuf `.toObject()` produced plain objects
// with JS numbers, which every consumer (charts, Date(), arithmetic) relies on.
// `plain` reproduces that: a deep clone with bigint -> Number and proto metadata
// stripped, so downstream code is unchanged.
function plain(value) {
  if (typeof value === 'bigint') return Number(value)
  if (Array.isArray(value)) return value.map(plain)
  if (value !== null && typeof value === 'object') {
    const out = {}
    for (const key of Object.keys(value)) {
      if (key.charCodeAt(0) === 36) continue
      out[key] = plain(value[key])
    }
    return out
  }
  return value
}

const mapEntries = (obj) => Object.entries(obj || {})

let _client = null

export function getClient() {
  if (!_client) {
    const baseUrl = environment.GRPC_URL || `https://api.${environment.GRPC_SERVER_HOSTNAME}:443/`
    const transport = createGrpcWebTransport({
      baseUrl,
      interceptors: [createMaintenanceInterceptor()],
    })
    _client = createClient(Streamline, transport)
  }
  return _client
}

// --- Health / Utility ---

export async function check(jwt) {
  await getClient().check({}, { headers: authHeaders(jwt) })
}

export async function clearCache(jwt) {
  await getClient().clearCache({}, { headers: authHeaders(jwt) })
}

export async function checkValidity(jwt) {
  const resp = await getClient().checkValidity({}, { headers: authHeaders(jwt) })
  return resp.isValid
}

// --- Aggregated Data ---

export async function getAllAggregatedData(jwt) {
  const resp = await getClient().getAllAggregatedData({}, { headers: authHeaders(jwt) })
  return { data: new Map(mapEntries(plain(resp.data))), lastUpdated: Number(resp.processingDate) }
}

export async function getAggregatedDataForPaths(jwt, paths) {
  const resp = await getClient().getAggregatedDataForPaths({ paths }, { headers: authHeaders(jwt) })
  return { dataMap: mapEntries(plain(resp.data)), processingDate: Number(resp.processingDate) }
}

export async function getActiveUnitTypePaths(jwt) {
  const resp = await getClient().getActiveUnitTypePaths({}, { headers: authHeaders(jwt) })
  return { unitTypePathsList: [...resp.unitTypePaths], processingDate: Number(resp.processingDate) }
}

// --- Site / Unit Data ---

export async function getSiteNames(jwt) {
  const resp = await getClient().getSiteNames({}, { headers: authHeaders(jwt) })
  return {
    names: new Map(mapEntries(plain(resp.idToName))),
    ingestionSources: new Map(mapEntries(plain(resp.idToIngestionSource))),
  }
}

export async function getSiteWhitelist(jwt) {
  const resp = await getClient().getSiteWhitelist({}, { headers: authHeaders(jwt) })
  return [...resp.whitelist]
}

export async function getUnitTypeAreas(jwt) {
  const resp = await getClient().getUnitTypeAreas({}, { headers: authHeaders(jwt) })
  return new Map(mapEntries(plain(resp.unitTypeIdToArea)))
}

export async function getSiteGeo(jwt, path) {
  const resp = await getClient().getSiteGeo({ path }, { headers: authHeaders(jwt) })
  const r = plain(resp)
  return {
    kss: new Map(mapEntries(r.kssData).map(([k, v]) => [k, { data: v.data, rentsMap: mapEntries(v.rents) }])),
    comp: new Map(mapEntries(r.competitorData).map(([k, v]) => [
      k,
      {
        competitorsList: (v.competitors || []).map(c => ({
          data: c.data,
          competitorStoreId: c.competitorStoreId,
          rentsMap: mapEntries(c.rents),
        })),
      },
    ])),
  }
}

// --- Timeseries ---

function createQueryColumn(dataKey) {
  switch (dataKey.type) {
    case 'forecast':
      return { column: { case: 'forecast', value: dataKey.key } }
    case 'snapshot':
      return { column: { case: 'snapshot', value: dataKey.key } }
    case 'historical-forecast':
      return { column: { case: 'histForecast', value: dataKey.key } }
    default:
      return { column: { case: undefined } }
  }
}

export async function getTimeseries(jwt, path, dataKey) {
  const resp = await getClient().getTimeseries(
    { path, column: createQueryColumn(dataKey), offsetDays: Math.floor(dataKey.dayOffset ?? 0) },
    { headers: authHeaders(jwt) },
  )
  return plain(resp.data)
}

function buildPathToColumn(pathKeyMap, type) {
  const pathToColumn = {}
  for (const [key, keys] of pathKeyMap.entries()) {
    const columnAndOffset = keys
      .filter(k => k.type === type)
      .map(k => ({ column: k.key, offsetDays: Math.floor(k.dayOffset ?? 0) }))
    if (columnAndOffset.length === 0) continue
    pathToColumn[key] = { columnAndOffset }
  }
  return pathToColumn
}

export async function getManySnapshots(jwt, pathKeyMap, windowDays = 365) {
  const pathToColumn = buildPathToColumn(pathKeyMap, 'snapshot')
  if (Object.keys(pathToColumn).length === 0) return new Map()
  const resp = await getClient().getManySnapshots(
    { pathToColumn, windowDays: BigInt(Math.floor(windowDays)) },
    { headers: authHeaders(jwt) },
  )
  return new Map(mapEntries(plain(resp.data)))
}

export async function getManyForecasts(jwt, pathKeyMap) {
  const pathToColumn = buildPathToColumn(pathKeyMap, 'forecast')
  if (Object.keys(pathToColumn).length === 0) return new Map()
  const resp = await getClient().getManyForecasts({ pathToColumn }, { headers: authHeaders(jwt) })
  return new Map(mapEntries(plain(resp.data)))
}

export async function getManyHistoricalForecasts(jwt, pathKeyMap) {
  const pathToColumn = buildPathToColumn(pathKeyMap, 'historical-forecast')
  if (Object.keys(pathToColumn).length === 0) return new Map()
  const resp = await getClient().getManyHistoricalForecasts({ pathToColumn }, { headers: authHeaders(jwt) })
  return new Map(mapEntries(plain(resp.data)))
}

export async function getManyTimeseries(jwt, pathKeyMap, windowDays = 365) {
  const [snap, fc, hfc] = await Promise.all([
    getManySnapshots(jwt, pathKeyMap, windowDays),
    getManyForecasts(jwt, pathKeyMap),
    getManyHistoricalForecasts(jwt, pathKeyMap),
  ])
  return new Map([...snap, ...fc, ...hfc])
}

export async function getRentalPriceHistory(jwt, ecrUnitId, rentalId) {
  const resp = await getClient().getRentalPriceHistory(
    { ecrUnitId: parseInt(ecrUnitId || '0', 10), rentalId: rentalId || '' },
    { headers: authHeaders(jwt) },
  )
  const data = plain(resp.data)
  if (data.length > 0) {
    const lastX = data[data.length - 1].x
    const todayMs = new Date().setHours(0, 0, 0, 0)
    if (lastX !== todayMs) {
      data.push({ y: data[data.length - 1].y, x: todayMs })
    }
  }
  return data
}

// --- WTF ---

export async function getWtf(jwt) {
  const resp = await getClient().getWtf({}, { headers: authHeaders(jwt) })
  return plain(resp.records).map(r => ({ ...r, variablesList: r.variables }))
}

export async function getWtfInputs(jwt) {
  const resp = await getClient().getWtfInputs({}, { headers: authHeaders(jwt) })
  return plain(resp)
}

export async function getWtfConfigurations(jwt) {
  const resp = await getClient().getWtfConfigurations({}, { headers: authHeaders(jwt) })
  return { configurationsList: plain(resp.configurations) }
}

export async function createWtfConfiguration(jwt, userId, configId, configJson) {
  const resp = await getClient().createWtfConfiguration(
    { configuration: { id: configId, userId, configuration: configJson } },
    { headers: authHeaders(jwt) },
  )
  return plain(resp)
}

export async function updateWtfConfiguration(jwt, userId, configId, configJson, activate = false) {
  const resp = await getClient().updateWtfConfiguration(
    { configuration: { id: configId, userId, configuration: configJson, active: activate } },
    { headers: authHeaders(jwt) },
  )
  return plain(resp)
}

export async function archiveWtfConfiguration(jwt, configId) {
  await getClient().archiveWtfConfiguration({ configurationId: configId }, { headers: authHeaders(jwt) })
}

export async function activateWtfConfiguration(jwt, configId) {
  await getClient().activateWtfConfiguration({ configurationId: configId }, { headers: authHeaders(jwt) })
}

export async function evaluateWtfConfiguration(jwt, configJson) {
  const resp = await getClient().evaluateWtfConfiguration({ configuration: configJson }, { headers: authHeaders(jwt) })
  return plain(resp)
}

export async function executeWtfConfiguration(jwt, configId, saveData = true) {
  const resp = await getClient().executeWtfConfiguration(
    { configurationId: configId, saveData },
    { headers: authHeaders(jwt) },
  )
  return plain(resp)
}

export async function validateWtfConfiguration(jwt, configJson) {
  const resp = await getClient().validateWtfConfiguration({ configuration: configJson }, { headers: authHeaders(jwt) })
  return plain(resp)
}

export async function validateWtfCompoundInput(jwt, expression) {
  const resp = await getClient().validateWtfCompoundInput({ expression }, { headers: authHeaders(jwt) })
  return plain(resp)
}

// --- Price Changes ---

export async function getPriceChanges(jwt) {
  const resp = await getClient().getPriceChanges({}, { headers: authHeaders(jwt) })
  return new Map(mapEntries(plain(resp.changes)))
}

export function watchPriceChanges(jwt, user, signal) {
  return getClient().watchPriceChanges(
    { name: user.name, email: user.email, userId: user.id },
    { headers: authHeaders(jwt), signal },
  )
}

export async function updatePriceChanges(jwt, changes, user) {
  await getClient().updatePriceChanges(
    { changes, user: { name: user.name, email: user.email, userId: user.id } },
    { headers: authHeaders(jwt) },
  )
}

// --- Flags ---

export async function getFlags(jwt, path) {
  const resp = await getClient().getFlags({ path }, { headers: authHeaders(jwt) })
  return plain(resp.flags)
}

export async function getManyFlags(jwt, paths) {
  const resp = await getClient().getManyFlags({ paths }, { headers: authHeaders(jwt) })
  return new Map(mapEntries(plain(resp.flags)).map(([k, v]) => [k, v.flags]))
}

// --- Pipeline ---

export async function getPipelineStatus(jwt) {
  const resp = await getClient().getPipelineStatus({}, { headers: authHeaders(jwt) })
  return plain(resp)
}

export async function forceDeployToStorman(jwt) {
  if (environment.production) throw new Error('Only available in test environment')
  await getClient().forceDeployToStorman({}, { headers: authHeaders(jwt) })
}

// --- Rentals / ECR ---

export async function getUnitRentals(jwt, path) {
  const resp = await getClient().getUnitRentals({ path }, { headers: authHeaders(jwt) })
  return plain(resp.data)
}

export async function getAllUnitRentalPriceChanges(jwt) {
  const resp = await getClient().getAllUnitRentalPriceChanges({}, { headers: authHeaders(jwt) })
  return plain(resp.rentals)
}

export async function updateRentalPriceChanges(jwt, updates, deployedBy) {
  const CHOICE_MAP = { auto: RentalPriceChoice.RECOMMENDATION, override: RentalPriceChoice.OVERRIDE, current: RentalPriceChoice.CURRENT_PRICE }
  const STATUS_MAP = { approved: PriceChangeStatus.APPROVED, rejected: PriceChangeStatus.REJECTED }

  const update = updates.map(u => {
    const change = {
      id: u.id,
      choice: CHOICE_MAP[u.choice] ?? RentalPriceChoice.RECOMMENDATION,
      status: STATUS_MAP[u.status] ?? PriceChangeStatus.UNDECIDED,
      snoozeMonths: Math.round(u.snoozeMonths ?? (u.status === 'approved' ? 12 : 1)),
    }
    if (u.override != null) change.override = Math.round(u.override)
    return change
  })
  const resp = await getClient().updateRentalPriceChanges({ deployedBy, update }, { headers: authHeaders(jwt) })
  return plain(resp.changed)
}

// --- ECR Config ---

export async function getGlobalEcrConfig(jwt) {
  const resp = await getClient().getGlobalEcrConfig({}, { headers: authHeaders(jwt) })
  return plain(resp.config)
}

export async function updateGlobalEcrConfig(jwt, configEntries) {
  const resp = await getClient().updateGlobalEcrConfig({ config: configEntries }, { headers: authHeaders(jwt) })
  return plain(resp.config)
}

export async function getExclusionEcrConfig(jwt) {
  const resp = await getClient().getExclusionEcrConfig({}, { headers: authHeaders(jwt) })
  return plain(resp.config)
}

export async function updateExclusionEcrConfig(jwt, configEntries) {
  const resp = await getClient().updateExclusionEcrConfig({ config: configEntries }, { headers: authHeaders(jwt) })
  return plain(resp.config)
}

export async function getTierBoundEcrConfig(jwt) {
  const resp = await getClient().getTierBoundEcrConfig({}, { headers: authHeaders(jwt) })
  return plain(resp.config)
}

export async function updateTierBoundEcrConfig(jwt, configEntries) {
  const resp = await getClient().updateTierBoundEcrConfig({ config: configEntries }, { headers: authHeaders(jwt) })
  return plain(resp.config)
}

// --- Competitor Weights ---

export async function getCompetitorWeights(jwt, siteId) {
  const resp = await getClient().getCompetitorWeights(siteId ? { siteId } : {}, { headers: authHeaders(jwt) })
  return plain(resp.weights)
}

export async function updateCompetitorWeights(jwt, updates) {
  await getClient().updateCompetitorWeights({ toUpdate: updates }, { headers: authHeaders(jwt) })
}

// --- User Management ---

export async function createUser(jwt, firstName, lastName, email, role) {
  const roleMap = { admin: Role.ADMIN, manager: Role.MANAGER, user: Role.USER }
  const resp = await getClient().createUser(
    { fname: firstName, lname: lastName, email, role: roleMap[role] ?? Role.USER },
    { headers: authHeaders(jwt) },
  )
  return plain(resp)
}

export async function disableUser(jwt, userId) {
  await getClient().disableUser({ userId }, { headers: authHeaders(jwt) })
}

export async function enableUser(jwt, userId) {
  await getClient().enableUser({ userId }, { headers: authHeaders(jwt) })
}

export async function forcePasswordReset(jwt, userId) {
  await getClient().forcePasswordReset({ userId }, { headers: authHeaders(jwt) })
}

export async function listUsers(jwt) {
  const resp = await getClient().listUsers({}, { headers: authHeaders(jwt) })
  return plain(resp.users).map(u => ({ ...u, rolesList: u.roles }))
}

export async function changeUserRole(jwt, userId, newRole) {
  await getClient().changeUserRole({ userId, newRole }, { headers: authHeaders(jwt) })
}

export async function resetUserMFA(jwt, userId) {
  await getClient().resetUserMFA({ userId }, { headers: authHeaders(jwt) })
}

// --- Version ---

export async function getVersionInfo(jwt) {
  const resp = await getClient().getVersionInfo({}, { headers: authHeaders(jwt) })
  return plain(resp)
}
